import React, { useState, useEffect, useCallback, useRef } from 'react';
import './sendFinalPerformaInvoice.css';

// ==================== COUNTRY CODES DATA ====================
// ==================== SEARCHABLE COUNTRY CODE SELECT ====================
const SearchableCountryCodeSelect = ({ value, onChange, countries = [] }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      setFilteredOptions(countries.filter(item =>
        item.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    } else {
      setFilteredOptions(countries);
    }
  }, [searchTerm, countries]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 100);
  };

  const handleSelect = (code) => {
    onChange({ target: { name: 'billToCountryCode', value: code } });
    setOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name: 'billToCountryCode', value: '' } });
    setSearchTerm('');
  };

  const selectedItem = countries.find(item => item.code === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem ? (selectedItem.flag + ' ' + selectedItem.code) : '')}
          onChange={(e) => { if (open) setSearchTerm(e.target.value); }}
          onClick={handleOpen}
          placeholder={!selectedItem ? 'Select country code' : ''}
          readOnly={!open}
          style={{
            width: '100%', padding: '12px 40px 12px 14px',
            border: open ? '2px solid #eb2329' : '1px solid #ddd',
            borderRadius: '4px', fontSize: '14px', outline: 'none',
            cursor: open ? 'text' : 'pointer', backgroundColor: 'white',
            transition: 'all 0.2s', height: '42px',
          }}
        />
        {value && !open && (
          <button onClick={handleClear} type="button" style={{
            position: 'absolute', right: '32px', top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px',
            display: 'flex', alignItems: 'center', color: '#9ca3af', zIndex: 2,
          }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#eb2329'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
          >
            <svg style={{ width: '16px', height: '16px', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <svg style={{
          position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
          width: '20px', height: '20px', color: open ? '#eb2329' : '#9ca3af', pointerEvents: 'none', zIndex: 2,
        }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {open
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />}
        </svg>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '300px',
          overflow: 'auto', zIndex: 1300, marginTop: '4px', backgroundColor: 'white',
          border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        }}>
          {filteredOptions.length === 0
            ? <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '13px' }}>No results found</div>
            : filteredOptions.map((item, index) => {
              const isSelected = value === item.code;
              return (
                <div key={item.code + '-' + index} onClick={() => handleSelect(item.code)} style={{
                  padding: '10px 12px', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s',
                  backgroundColor: isSelected ? 'rgba(235,35,41,0.1)' : 'white',
                  color: isSelected ? '#eb2329' : '#374151',
                  borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isSelected ? 'rgba(235,35,41,0.15)' : 'rgba(235,35,41,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? 'rgba(235,35,41,0.1)' : 'white'; }}
                >
                  <span style={{ fontSize: '16px' }}>{item.flag}</span>
                  <span>{item.code}</span>
                  <span style={{ marginLeft: 'auto', color: isSelected ? '#eb2329' : '#6b7280', fontSize: '12px' }}>{item.country}</span>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

// ==================== SEARCHABLE COUNTRY SELECT ====================
// KEY FIX: accepts `displayName` prop so it shows the name even before options load
const SearchableCountrySelect = ({ value, displayName, options, onChange, disabled = false, error = false }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Try to find in options first; fall back to displayName passed from parent
  const selectedItem = (options || []).find(o => String(o.countryId) === String(value));
  const shownName = selectedItem ? selectedItem.countryName : (displayName || '');

  const filteredOptions = searchTerm
    ? (options || []).filter(o => o.countryName && o.countryName.toLowerCase().includes(searchTerm.toLowerCase()))
    : (options || []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 100);
  };

  const handleSelect = (option) => {
    onChange({ target: { name: 'countryId', value: String(option.countryId) } });
    onChange({ target: { name: 'shipToCountry', value: option.countryName } });
    setOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name: 'countryId', value: '' } });
    onChange({ target: { name: 'shipToCountry', value: '' } });
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          // THE KEY LINE: show shownName (from options OR displayName) when closed
          value={open ? searchTerm : shownName}
          onChange={(e) => { if (open) setSearchTerm(e.target.value); }}
          onClick={handleOpen}
          placeholder={shownName ? '' : 'Search country...'}
          readOnly={!open}
          disabled={disabled}
          style={{
            width: '100%', padding: '12px 40px 12px 14px',
            border: open ? '2px solid #eb2329' : (error ? '2px solid #eb2329' : '1px solid #ddd'),
            borderRadius: '4px', fontSize: '14px', outline: 'none',
            cursor: disabled ? 'not-allowed' : (open ? 'text' : 'pointer'),
            backgroundColor: disabled ? '#f5f5f5' : 'white',
            transition: 'all 0.2s', height: '42px',
          }}
        />
        {(value || displayName) && !open && !disabled && (
          <button onClick={handleClear} type="button" style={{
            position: 'absolute', right: '32px', top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px',
            display: 'flex', alignItems: 'center', color: '#9ca3af', zIndex: 2,
          }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#eb2329'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
          >
            <svg style={{ width: '16px', height: '16px', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <svg style={{
          position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
          width: '20px', height: '20px', color: open ? '#eb2329' : '#9ca3af', pointerEvents: 'none', zIndex: 2,
        }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {open
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />}
        </svg>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '300px',
          overflow: 'auto', zIndex: 1300, marginTop: '4px', backgroundColor: 'white',
          border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        }}>
          {filteredOptions.length === 0
            ? <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '13px' }}>No results found</div>
            : filteredOptions.map((option, index) => {
              const isSelected = String(option.countryId) === String(value);
              return (
                <div key={option.countryId} onClick={() => handleSelect(option)} style={{
                  padding: '10px 12px', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s',
                  backgroundColor: isSelected ? 'rgba(235,35,41,0.1)' : 'white',
                  color: isSelected ? '#eb2329' : '#374151',
                  borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isSelected ? 'rgba(235,35,41,0.15)' : 'rgba(235,35,41,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? 'rgba(235,35,41,0.1)' : 'white'; }}
                >
                  {option.countryName}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

// ==================== SEARCHABLE PORT SELECT ====================
// KEY FIX: accepts `displayName` prop so it shows the name even before options load
const SearchablePortSelect = ({ value, displayName, options, onChange, disabled = false, error = false }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const selectedItem = (options || []).find(o => String(o.portId) === String(value));
  const shownName = selectedItem ? selectedItem.portName : (displayName || '');

  const filteredOptions = searchTerm
    ? (options || []).filter(o => o.portName && o.portName.toLowerCase().includes(searchTerm.toLowerCase()))
    : (options || []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 100);
  };

  const handleSelect = (option) => {
    onChange({ target: { name: 'portId', value: String(option.portId) } });
    onChange({ target: { name: 'shipToPort', value: option.portName } });
    setOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name: 'portId', value: '' } });
    onChange({ target: { name: 'shipToPort', value: '' } });
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          // THE KEY LINE: show shownName (from options OR displayName) when closed
          value={open ? searchTerm : shownName}
          onChange={(e) => { if (open) setSearchTerm(e.target.value); }}
          onClick={handleOpen}
          placeholder={shownName ? '' : (disabled ? 'Select a country first' : 'Search port...')}
          readOnly={!open}
          disabled={disabled}
          style={{
            width: '100%', padding: '12px 40px 12px 14px',
            border: open ? '2px solid #eb2329' : ((error && !disabled) ? '2px solid #eb2329' : '1px solid #ddd'),
            borderRadius: '4px', fontSize: '14px', outline: 'none',
            cursor: disabled ? 'not-allowed' : (open ? 'text' : 'pointer'),
            backgroundColor: disabled ? '#f5f5f5' : 'white',
            transition: 'all 0.2s', height: '42px',
          }}
        />
        {(value || displayName) && !open && !disabled && (
          <button onClick={handleClear} type="button" style={{
            position: 'absolute', right: '32px', top: '50%', transform: 'translateY(-50%)',
            background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px',
            display: 'flex', alignItems: 'center', color: '#9ca3af', zIndex: 2,
          }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#eb2329'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
          >
            <svg style={{ width: '16px', height: '16px', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        <svg style={{
          position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
          width: '20px', height: '20px', color: open ? '#eb2329' : '#9ca3af', pointerEvents: 'none', zIndex: 2,
        }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {open
            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />}
        </svg>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '300px',
          overflow: 'auto', zIndex: 1300, marginTop: '4px', backgroundColor: 'white',
          border: '1px solid #e5e7eb', borderRadius: '4px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        }}>
          {filteredOptions.length === 0
            ? <div style={{ padding: '10px 12px', color: '#6b7280', fontSize: '13px' }}>No ports found</div>
            : filteredOptions.map((option, index) => {
              const isSelected = String(option.portId) === String(value);
              return (
                <div key={option.portId} onClick={() => handleSelect(option)} style={{
                  padding: '10px 12px', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s',
                  backgroundColor: isSelected ? 'rgba(235,35,41,0.1)' : 'white',
                  color: isSelected ? '#eb2329' : '#374151',
                  borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isSelected ? 'rgba(235,35,41,0.15)' : 'rgba(235,35,41,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? 'rgba(235,35,41,0.1)' : 'white'; }}
                >
                  {option.portName}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const SendFinalPerformaInvoice = ({ inquiryData, onBack, onInvoiceCreated }) => {
  const isEditMode = !!(inquiryData && inquiryData.isEditMode);

  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [createdInvoiceData, setCreatedInvoiceData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [phoneCodes, setPhoneCodes] = useState([]);
  const [ports, setPorts] = useState([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [isLoadingPorts, setIsLoadingPorts] = useState(false);

  const [formData, setFormData] = useState({
    inquiryId: null,
    proformaInvoiceNo: '',
    companyName: 'AMAR ENTERPRISES CO LTD',
    companyAddress: 'Amar Enterprises Co Ltd, 1076-1 Ooaza Komizou Iwatsuki Saitama Shi Saitama-Ken -339-0003 Japan',
    companyPhone: '+81-48-793-3911',
    companyGSTIN: '08AALCR2857A1ZD',
    companyPAN: 'AVHPC9999A',
    invoiceDate: new Date().toISOString().split('T')[0],
    billToName: '',
    billToEmail: '',
    billToCountryCode: '',
    billToPhone: '',
    shipToCountry: '',
    shipToPort: '',
    countryId: null,
    portId: null,
    vehicleBrand: '',
    vehicleModel: '',
    chassisNumber: '',
    vehicleYear: '',
    shippingOption: '',
    volume: '',
    color: '',
    engineCC: '',
    vehicleCost: '',
    carModelId: null,
    shippingType: '',
    shippingCost: '',
    bankAccountHolder: 'AMAR ENTERPRISES CO LTD',
    bankAccountNumber: '71******',
    bankName: 'SUMITOMO MITSUI BANKING CORPORATION',
    bankBranch: 'KASUKABE BRANCH, JAPAN',
    bankSwiftCode: 'SMBCJPJT',
    bankAccountType: 'Ordinary Account',
    contactPerson: 'Mr Raj Kumar Sharma',
    contactMobile: '+81-90-89-453119',
    termsConditions: '1. No return policy applies\n2. Customer will pay the delivery charges\n3. Payment due within 15 days\n4. All prices are in USD',
  });

  // Ports filtered by selected country
  const filteredPorts = formData.countryId
    ? ports.filter(function(port) { return port.countryId === parseInt(formData.countryId); })
    : [];

  const fetchCountriesAndPorts = async () => {
    setIsLoadingCountries(true);
    setIsLoadingPorts(true);
    try {
      const token = localStorage.getItem('adminAuthToken');

      // Fetch shipping countries (for Ship To dropdown)
      const countriesResponse = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries', {
        method: 'GET',
        headers: { accept: 'application/json', 'Accept-Language': 'en' },
      });
      const countriesData = await countriesResponse.json();
      if (countriesResponse.ok && Array.isArray(countriesData)) setCountries(countriesData);

      // Fetch all-countries for phone codes (for Bill To Country Code)
      const phoneRes = await fetch('https://car.amar.co.jp/api/authorization/shipping/all-countries', {
        headers: { accept: 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${token}` }
      });
      if (phoneRes.ok) {
        const phoneData = await phoneRes.json();
        const mapped = phoneData
          .map(c => ({ code: c.countryPhoneCode, country: c.countryName, flag: c.isoCode }))
          .sort((a, b) => a.country.localeCompare(b.country));
        setPhoneCodes(mapped);
      }

      const portsResponse = await fetch('https://car.amar.co.jp/api/authorization/shipping/ports', {
        method: 'GET',
        headers: { accept: 'application/json', 'Accept-Language': 'en' },
      });
      const portsData = await portsResponse.json();
      if (portsResponse.ok && Array.isArray(portsData)) setPorts(portsData);
    } catch (error) {
      console.error('Error fetching countries/ports:', error);
    } finally {
      setIsLoadingCountries(false);
      setIsLoadingPorts(false);
    }
  };

  useEffect(() => {
    fetchCountriesAndPorts();
  }, []);

  useEffect(() => {
    if (inquiryData) {
      var firstName   = inquiryData.firstName   || inquiryData.guestFirstName || '';
      var lastName    = inquiryData.lastName    || inquiryData.guestLastName  || '';
      var phone       = inquiryData.guestMobileNumber || inquiryData.userMobileNumber || '';
      var email       = inquiryData.userEmailId      || inquiryData.guestEmailId      || '';
      var countryCode = inquiryData.guestCountryCode || inquiryData.countryCode || '';

      setFormData(function(prev) {
        return Object.assign({}, prev, {
          proformaInvoiceNo: inquiryData.proformaInvoiceNo || '',
          inquiryId:         inquiryData.inquiryRefNumber  || null,
          billToName:        (firstName + ' ' + lastName).trim(),
          billToEmail:       email,
          billToCountryCode: countryCode,
          billToPhone:       phone,
          shipToCountry:     inquiryData.countryName || '',
          shipToPort:        inquiryData.portName    || '',
          countryId:         inquiryData.countryId   || null,
          portId:            inquiryData.portId      || null,
          vehicleBrand:      inquiryData.carBrandName       || '',
          vehicleModel:      inquiryData.carModelName       || '',
          chassisNumber:     inquiryData.carChassisNumber   || '',
          vehicleYear:       inquiryData.carManufactureDate ? inquiryData.carManufactureDate.split('-')[0] : '',
          shippingOption:    inquiryData.shippingOption     || '',
          volume:            inquiryData.volumeCubicMeters  || inquiryData.m3 || inquiryData.carLength || '',
          color:             inquiryData.carColor           || '',
          engineCC:          inquiryData.carEngineCC        || '',
          vehicleCost:       inquiryData.carPrice           || '',
          shippingCost:      inquiryData.baseShippingCost   || '',
          shippingType:      inquiryData.shippingType       || 'individual',
          carModelId:        inquiryData.carModelId         || null,
        });
      });
    }
  }, [inquiryData]);

  // The inquiry API response doesn't always include a countryId (only countryName).
  // Once the countries list has loaded, resolve the matching countryId by name
  // so the form validates correctly and the Port dropdown isn't stuck disabled.
  useEffect(() => {
    if (countries.length > 0 && inquiryData && inquiryData.countryName) {
      setFormData(function(prev) {
        if (prev.countryId) return prev; // already resolved (or user picked manually)
        var matched = countries.find(function(c) {
          return c.countryName && c.countryName.trim().toLowerCase() === inquiryData.countryName.trim().toLowerCase();
        });
        return matched ? Object.assign({}, prev, { countryId: matched.countryId }) : prev;
      });
    }
  }, [countries, inquiryData]);

  // Same fallback for portId in case an inquiry only provides portName.
  useEffect(() => {
    if (ports.length > 0 && inquiryData && inquiryData.portName) {
      setFormData(function(prev) {
        if (prev.portId) return prev;
        var matched = ports.find(function(p) {
          return p.portName && p.portName.trim().toLowerCase() === inquiryData.portName.trim().toLowerCase();
        });
        return matched ? Object.assign({}, prev, { portId: matched.portId }) : prev;
      });
    }
  }, [ports, inquiryData]);

  const handleInputChange = (e) => {
    var name  = e.target.name;
    var value = e.target.value;
    if (name === 'billToPhone') {
      var digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 15) setFormData(function(prev) { return Object.assign({}, prev, { billToPhone: digitsOnly }); });
    } else if (name === 'countryId') {
      setFormData(function(prev) { return Object.assign({}, prev, { countryId: value, portId: null, shipToPort: '' }); });
    } else {
      setFormData(function(prev) { return Object.assign({}, prev, { [name]: value }); });
    }
    if (errors[name]) setErrors(function(prev) { return Object.assign({}, prev, { [name]: '' }); });
  };

  const calculateGrandTotal = () => {
    var vehicleCost  = parseFloat(formData.vehicleCost)  || 0;
    var shippingCost = parseFloat(formData.shippingCost) || 0;
    return (vehicleCost + shippingCost).toFixed(2);
  };

  const showError   = (msg) => { setErrorMessage(msg);   setShowErrorDialog(true);   };
  const showSuccess = (msg, invoiceData) => { setSuccessMessage(msg); setCreatedInvoiceData(invoiceData); setShowSuccessDialog(true); };

  const validateForm = () => {
    var newErrors = {};
    var missing   = [];
    if (!formData.invoiceDate) { newErrors.invoiceDate = 'Required'; missing.push('Proforma Invoice Date'); }
    if (isEditMode && !formData.proformaInvoiceNo.trim()) { newErrors.proformaInvoiceNo = 'Required'; missing.push('Proforma Invoice No'); }
    if (!formData.billToName.trim()) { newErrors.billToName = 'Required'; missing.push('Customer Name'); }
    if (!formData.billToEmail.trim()) { newErrors.billToEmail = 'Required'; missing.push('Email'); }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billToEmail)) { newErrors.billToEmail = 'Invalid email'; missing.push('Valid Email'); }
    if (!formData.billToCountryCode.trim()) { newErrors.billToCountryCode = 'Required'; missing.push('Country Code'); }
    if (!formData.billToPhone.trim()) { newErrors.billToPhone = 'Required'; missing.push('Phone Number'); }
    else if (formData.billToPhone.length < 10) { newErrors.billToPhone = 'Minimum 10 digits required'; missing.push('Valid Phone Number (min 10 digits)'); }
    if (!formData.countryId) { newErrors.countryId = 'Required'; missing.push('Country'); }
    if (!formData.portId)    { newErrors.portId    = 'Required'; missing.push('Port'); }
    if (formData.shippingCost === '' || formData.shippingCost === null || formData.shippingCost === undefined || isNaN(parseFloat(formData.shippingCost)) || parseFloat(formData.shippingCost) < 0) { newErrors.shippingCost = 'Required'; missing.push('Shipping Cost'); }
    setErrors(newErrors);
    if (missing.length > 0) { showError('Please fill in the following required fields:\n\n' + missing.join(', ')); return false; }
    return true;
  };

  const formatDateForAPI = (dateString) => {
    var date  = new Date(dateString);
    var day   = String(date.getDate()).padStart(2, '0');
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var year  = date.getFullYear();
    return day + '-' + month + '-' + year;
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      var el = document.querySelector('.sfpi-form-group input.sfpi-error, .sfpi-form-group textarea.sfpi-error');
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
    }, 100);
  };

  const handleSendInvoice = async () => {
    if (!validateForm()) { setShowPreview(false); scrollToFirstError(); return; }
    setLoading(true);
    try {
      if (isEditMode) { await handleUpdateInvoice(); } else { await handleCreateInvoice(); }
    } finally { setLoading(false); }
  };

  const handleUpdateInvoice = async () => {
    var requestBody = {
      proformaInvoiceNo:   formData.proformaInvoiceNo.trim(),
      proformaInvoiceDate: formatDateForAPI(formData.invoiceDate),
      customerName:        formData.billToName.trim(),
      email:               formData.billToEmail.trim(),
      phoneNumber:         formData.billToCountryCode + '-' + formData.billToPhone,
      countryId:           parseInt(formData.countryId),
      portId:              parseInt(formData.portId),
      carModelId:          formData.carModelId || (inquiryData && inquiryData.carModelId) || 1,
      m3:                  parseFloat(formData.volume) || 0,
      shippingOption:      formData.shippingOption || 'CIF',
      shippingCost:        parseFloat(formData.shippingCost),
      grandTotal:          parseFloat(calculateGrandTotal()),
    };
    var response = await fetch('https://car.amar.co.jp/api/authorization/api/proforma-invoice/update', {
      method: 'PUT',
      headers: { accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    var data;
    try { data = await response.json(); } catch (e) { throw new Error('Invalid response from server'); }
    if (response.ok) {
      showSuccess(
        '✅ Invoice updated successfully!\n\n📋 Proforma Invoice No: ' + data.proformaInvoiceNo + '\n🆔 Invoice ID: ' + data.proformaInvoiceId + '\n💰 Total Amount: $' + data.grandTotal + '\n📅 Invoice Date: ' + data.proformaInvoiceDate + '\n👤 Customer: ' + data.customerName + '\n\n' + (data.message || ''),
        { invoiceId: data.proformaInvoiceId, proformaInvoiceId: data.proformaInvoiceId, proformaInvoiceNo: data.proformaInvoiceNo, invoiceDate: data.proformaInvoiceDate, customerName: data.customerName, email: data.email, phoneNumber: data.phoneNumber, grandTotal: data.grandTotal, shippingCost: data.shippingCost, shippingOption: data.shippingOption, m3: data.m3, countryId: data.countryId, portId: data.portId, carModelId: data.carModelId, countryName: formData.shipToCountry, portName: formData.shipToPort, vehicleCost: formData.vehicleCost }
      );
    } else if (response.status === 404) { showError('❌ Invoice Not Found!\n\n' + (data.error || data.message || 'The invoice could not be found.')); setShowPreview(false);
    } else if (response.status === 400) { showError('⚠️ Validation Error!\n\n' + (data.error || data.message || 'Please check all fields and try again.')); setShowPreview(false);
    } else if (response.status === 500) { showError('🚨 Server Error!\n\nThere was an internal server error. Please try again later or contact support.');
    } else { showError('❌ Error ' + response.status + '\n\n' + (data.error || data.message || 'Failed to update invoice. Please try again.')); }
  };

  const handleCreateInvoice = async () => {
    var requestBody = {
      proformaInvoiceDate: formatDateForAPI(formData.invoiceDate),
      customerName:  formData.billToName.trim(),
      email:         formData.billToEmail.trim(),
      phoneNumber:   formData.billToCountryCode + '-' + formData.billToPhone,
      countryId:     parseInt(formData.countryId),
      portId:        parseInt(formData.portId),
      carModelId:    formData.carModelId || (inquiryData && inquiryData.carModelId) || 1,
      m3:            parseFloat(formData.volume) || 0,
      shippingOption: formData.shippingOption || 'CIF',
      shippingCost:  parseFloat(formData.shippingCost),
      grandTotal:    parseFloat(calculateGrandTotal()),
      invoiceId:     formData.inquiryId || (inquiryData && inquiryData.inquiryRefNumber) || null,
    };
    var response = await fetch('https://car.amar.co.jp/api/authorization/api/proforma-invoice/save', {
      method: 'POST',
      headers: { accept: '*/*', 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    var data;
    try { data = await response.json(); } catch (e) { throw new Error('Invalid response from server'); }
    if (response.status === 201 || response.ok) {
      var invoiceId         = data.invoiceId || data.id || data.proformaInvoiceId;
      var proformaInvoiceNo = data.proformaInvoiceNo || 'Auto-generated';
      var invoiceResponseData = { invoiceId: invoiceId, proformaInvoiceId: invoiceId, proformaInvoiceNo: proformaInvoiceNo, invoiceDate: requestBody.proformaInvoiceDate, customerName: requestBody.customerName, email: requestBody.email, phoneNumber: requestBody.phoneNumber, grandTotal: requestBody.grandTotal, shippingCost: requestBody.shippingCost, shippingOption: requestBody.shippingOption, m3: requestBody.m3, inquiryId: inquiryData && inquiryData.id, carModelId: requestBody.carModelId, countryId: requestBody.countryId, portId: requestBody.portId, countryName: formData.shipToCountry, portName: formData.shipToPort, vehicleCost: formData.vehicleCost };
      // No success dialog here by design — go straight to the parent's
      // onInvoiceCreated handler, which should redirect to All Final Invoices
      // and auto-open this invoice's View modal (see allFinalPerformaInvoice.js's
      // autoOpenId prop).
      if (onInvoiceCreated) onInvoiceCreated(invoiceResponseData);
    } else if (response.status === 409) { showError('❌ Duplicate invoice detected!\n\n' + (data.error || data.message) + '\n\nPlease try again.'); setShowPreview(false);
    } else if (response.status === 400) { showError('⚠️ Validation Error!\n\n' + (data.error || data.message || 'Please check all fields and try again.')); setShowPreview(false);
    } else if (response.status === 500) { showError('🚨 Server Error!\n\nThere was an internal server error. Please try again later or contact support.');
    } else { showError('❌ Error ' + response.status + '\n\n' + (data.error || data.message || 'Failed to create invoice. Please try again.')); }
  };

  const handlePreview = () => {
    if (!validateForm()) { scrollToFirstError(); return; }
    setShowPreview(true);
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    if (onBack) onBack();
  };

  var pageTitle    = isEditMode ? 'Edit Proforma Invoice' : 'Send Final Proforma Invoice';
  var sendBtnLabel = loading
    ? (isEditMode ? '⏳ Updating Invoice...' : '⏳ Creating Invoice...')
    : (isEditMode ? '💾 Update Invoice'      : '📧 Create & Send Invoice');

  return (
    <div className="sfpi-container">
      {/* Error Dialog */}
      {showErrorDialog && (
        <div className="sfpi-dialog-overlay" onClick={() => setShowErrorDialog(false)}>
          <div className="sfpi-dialog-box sfpi-error-dialog" onClick={function(e) { e.stopPropagation(); }}>
            <div className="sfpi-dialog-header sfpi-error"><span className="sfpi-dialog-icon">⚠️</span><h3>Error</h3></div>
            <div className="sfpi-dialog-body"><p style={{ whiteSpace: 'pre-line' }}>{errorMessage}</p></div>
            <div className="sfpi-dialog-footer"><button className="sfpi-btn-dialog-primary" onClick={() => setShowErrorDialog(false)}>OK</button></div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {showSuccessDialog && createdInvoiceData && (
        <div className="sfpi-dialog-overlay" onClick={handleSuccessDialogClose}>
          <div className="sfpi-dialog-box sfpi-success-dialog" onClick={function(e) { e.stopPropagation(); }}>
            <div className="sfpi-dialog-header sfpi-success"><span className="sfpi-dialog-icon">✅</span><h3>{isEditMode ? 'Invoice Updated Successfully!' : 'Invoice Created Successfully!'}</h3></div>
            <div className="sfpi-dialog-body">
              <p style={{ whiteSpace: 'pre-line' }}>{successMessage}</p>
              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #0ea5e9', fontSize: '14px' }}>
                <div style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '120px' }}>Invoice ID:</strong><span style={{ fontSize: '16px', color: '#0284c7', fontWeight: 'bold', backgroundColor: '#e0f2fe', padding: '4px 10px', borderRadius: '4px' }}>{createdInvoiceData.invoiceId}</span></div>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '120px' }}>Invoice Number:</strong><span style={{ fontWeight: 'bold', color: '#059669' }}>{createdInvoiceData.proformaInvoiceNo}</span></div>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '120px' }}>Total Amount:</strong><span style={{ color: '#059669', fontWeight: 'bold' }}>${createdInvoiceData.grandTotal}</span></div>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}><strong style={{ minWidth: '120px' }}>Customer:</strong><span>{createdInvoiceData.customerName}</span></div>
              </div>
            </div>
            <div className="sfpi-dialog-footer">
              <button className="sfpi-btn-dialog-secondary" onClick={handleSuccessDialogClose}>Close</button>
              {!isEditMode && (<button className="sfpi-btn-dialog-primary" onClick={function() { handleSuccessDialogClose(); if (onInvoiceCreated) onInvoiceCreated(createdInvoiceData); }}>Proceed to Order</button>)}
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="sfpi-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isEditMode && (
            <span style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Edit Mode</span>
          )}
          <h2>{pageTitle}</h2>
        </div>
        <div className="sfpi-header-actions">
          <button className="sfpi-btn-back" onClick={onBack}>← Back to Inquiries</button>
          <button className="sfpi-btn-preview" onClick={handlePreview}>👁 Preview Invoice</button>
        </div>
      </div>

      {!showPreview ? (
        <div className="sfpi-invoice-form">

          {/* Invoice Details */}
          <div className="sfpi-form-section">
            <h3 className="sfpi-section-title">Invoice Details</h3>
            <div className="sfpi-form-grid">
              <div className="sfpi-form-group">
                <label>Proforma Invoice Date <span className="sfpi-required-star">*</span></label>
                <input type="date" name="invoiceDate" value={formData.invoiceDate} onChange={handleInputChange} className={errors.invoiceDate ? 'sfpi-error' : ''} />
                {errors.invoiceDate && <span className="sfpi-error-message">{errors.invoiceDate}</span>}
              </div>
              <div className="sfpi-form-group">
                <label>Proforma Invoice No</label>
                {isEditMode ? (
                  <>
                    <input type="text" value={formData.proformaInvoiceNo} readOnly style={{ fontStyle: 'italic', color: '#6c757d', background: '#f8f9fa' }} />
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>Invoice number cannot be changed</small>
                  </>
                ) : (
                  <>
                    <input type="text" value="Auto-generated by system" readOnly style={{ fontStyle: 'italic', color: '#6c757d' }} />
                    <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>Invoice number will be automatically generated after creation</small>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="sfpi-form-section">
            <h3 className="sfpi-section-title">BILL TO</h3>
            <div className="sfpi-form-grid">
              <div className="sfpi-form-group">
                <label>Customer Name <span className="sfpi-required-star">*</span></label>
                <input type="text" name="billToName" value={formData.billToName} onChange={handleInputChange} placeholder="Enter customer name" className={errors.billToName ? 'sfpi-error' : ''} />
                {errors.billToName && <span className="sfpi-error-message">{errors.billToName}</span>}
              </div>
              <div className="sfpi-form-group">
                <label>Email <span className="sfpi-required-star">*</span></label>
                <input type="email" name="billToEmail" value={formData.billToEmail} onChange={handleInputChange} placeholder="Enter email" className={errors.billToEmail ? 'sfpi-error' : ''} />
                {errors.billToEmail && <span className="sfpi-error-message">{errors.billToEmail}</span>}
              </div>
              <div className="sfpi-form-group">
                <label>Country Code <span className="sfpi-required-star">*</span></label>
                <SearchableCountryCodeSelect value={formData.billToCountryCode} onChange={handleInputChange} countries={phoneCodes} />
                {errors.billToCountryCode && <span className="sfpi-error-message">{errors.billToCountryCode}</span>}
              </div>
              <div className="sfpi-form-group">
                <label>Phone Number <span className="sfpi-required-star">*</span></label>
                <input type="tel" name="billToPhone" value={formData.billToPhone} onChange={handleInputChange} placeholder="Enter phone number (min 10 digits)" maxLength="15" className={errors.billToPhone ? 'sfpi-error' : ''} />
                {errors.billToPhone && <span className="sfpi-error-message">{errors.billToPhone}</span>}
                <small style={{ color: '#666', fontSize: '11px', marginTop: '2px', display: 'block' }}>{(formData.billToPhone && formData.billToPhone.length) || 0}/15 digits</small>
              </div>
            </div>
          </div>

          {/* Ship To */}
          <div className="sfpi-form-section">
            <h3 className="sfpi-section-title">SHIP TO</h3>
            <div className="sfpi-form-grid">

              {/* Country — always a dropdown, pre-filled via displayName */}
              <div className="sfpi-form-group">
                <label>Country <span className="sfpi-required-star">*</span></label>
                <SearchableCountrySelect
                  value={formData.countryId ? String(formData.countryId) : ''}
                  displayName={formData.shipToCountry}
                  options={countries}
                  onChange={handleInputChange}
                  disabled={isLoadingCountries}
                  error={!formData.countryId}
                />
                {isLoadingCountries && <small style={{ color: '#999' }}>Loading countries...</small>}
                {errors.countryId && <span className="sfpi-error-message">{errors.countryId}</span>}
              </div>

              {/* Port — always a dropdown, pre-filled via displayName */}
              <div className="sfpi-form-group">
                <label>Port <span className="sfpi-required-star">*</span></label>
                <SearchablePortSelect
                  value={formData.portId ? String(formData.portId) : ''}
                  displayName={formData.shipToPort}
                  options={filteredPorts}
                  onChange={handleInputChange}
                  disabled={isLoadingPorts || !formData.countryId}
                  error={!formData.portId}
                />
                {!formData.countryId && !isLoadingPorts && <small style={{ color: '#999' }}>Please select a country first</small>}
                {isLoadingPorts && <small style={{ color: '#999' }}>Loading ports...</small>}
                {errors.portId && <span className="sfpi-error-message">{errors.portId}</span>}
              </div>

              <div className="sfpi-form-group">
                <label>Shipping Type</label>
                <input type="text" name="shippingType" value={formData.shippingType} onChange={handleInputChange} placeholder="Enter shipping type" />
              </div>
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="sfpi-form-section">
            <h3 className="sfpi-section-title">Vehicle Details</h3>
            <div className="sfpi-form-grid">
              <div className="sfpi-form-group"><label>Brand</label><input type="text" value={formData.vehicleBrand} readOnly /></div>
              <div className="sfpi-form-group"><label>Model</label><input type="text" value={formData.vehicleModel} readOnly /></div>
              <div className="sfpi-form-group"><label>Chassis Number</label><input type="text" value={formData.chassisNumber} readOnly /></div>
              {/* <div className="sfpi-form-group"><label>Shipping Option</label><input type="text" value={formData.shippingOption} readOnly /></div> */}
              <div className="sfpi-form-group"><label>Volume (m³)</label><input type="text" value={formData.volume} readOnly /></div>
              <div className="sfpi-form-group"><label>Color</label><input type="text" value={formData.color} readOnly /></div>
              <div className="sfpi-form-group"><label>Engine CC</label><input type="text" value={formData.engineCC} readOnly /></div>
              <div className="sfpi-form-group"><label>Vehicle Cost ($)</label><input type="number" value={formData.vehicleCost} readOnly /></div>
            </div>
          </div>

          {/* Shipping Cost */}
          <div className="sfpi-form-section">
            <h3 className="sfpi-section-title">Shipping Cost</h3>
            <div className="sfpi-form-grid">
              <div className="sfpi-form-group"><label>Car Basic Price ($)</label><input type="number" value={formData.vehicleCost} readOnly /></div>
              <div className="sfpi-form-group">
                <label>Shipping Cost ($) <span className="sfpi-required-star">*</span></label>
                <input type="number" name="shippingCost" value={formData.shippingCost} onChange={handleInputChange} placeholder="Enter shipping cost" className={(errors.shippingCost || formData.shippingCost === '' || formData.shippingCost === null || formData.shippingCost === undefined) ? 'sfpi-error' : ''} step="0.01" />
                {errors.shippingCost && <span className="sfpi-error-message">{errors.shippingCost}</span>}
              </div>
              <div className="sfpi-form-group sfpi-grand-total-display">
                <label>Grand Total ($)</label>
                <div className="sfpi-grand-total-value">$ {calculateGrandTotal()}</div>
              </div>
            </div>
          </div>

          {/* Terms */}
          {/* <div className="sfpi-form-section">
            <h3 className="sfpi-section-title">Notes & Terms Conditions</h3>
            <div className="sfpi-form-group sfpi-full-width">
              <textarea name="termsConditions" value={formData.termsConditions} onChange={handleInputChange} rows="6" placeholder="Enter terms and conditions" />
            </div>
          </div> */}

          {/* Actions */}
          <div className="sfpi-form-actions">
            <button className="sfpi-btn-cancel" onClick={onBack} disabled={loading}>Cancel</button>
            <button className="sfpi-btn-send" onClick={handleSendInvoice} disabled={loading}>{sendBtnLabel}</button>
          </div>

        </div>
      ) : (
        <div className="sfpi-invoice-preview">
          <div className="sfpi-preview-header">
            <h3>Invoice Preview</h3>
            <button className="sfpi-btn-edit" onClick={() => setShowPreview(false)} disabled={loading}>✏️ Edit Invoice</button>
          </div>
          <div className="sfpi-preview-actions">
            <button className="sfpi-btn-edit" onClick={() => setShowPreview(false)} disabled={loading}>✏️ Edit Invoice</button>
            <button className="sfpi-btn-send" onClick={handleSendInvoice} disabled={loading}>{sendBtnLabel}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendFinalPerformaInvoice;